import React, { type ReactNode } from 'react';
import InformationIcon from '@atlaskit/icon/core/information';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import Link from '@atlaskit/primitives/anchor';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { SupportedThirdParty } from '../../../../common/constants/import-type';
import { SectionHeading } from '../../../../common/ui';
import { useContentForThirdParty, useUrlForThirdParty } from '../../../../common/utils';
import {
	useProjectData,
	useThirdPartyFieldsIncludedState,
} from '../../../../controllers/import-state';
import messages from './messages';

export const FieldsMappingHeader = ({ thirdParty }: { thirdParty: SupportedThirdParty }) => {
	const { formatMessage } = useIntl();
	const { fieldsMappingPageDescription, fieldsMappingPageDisclaimer } =
		useContentForThirdParty(thirdParty);
	const [{ type }] = useProjectData();
	const { fieldURL, customFieldURL, fieldMappingURL } = useUrlForThirdParty(thirdParty, type);
	const [{ hasUnsupportedFieldFiltered }] = useThirdPartyFieldsIncludedState();

	return (
		<>
			<SectionHeading>{formatMessage(messages.heading)}</SectionHeading>
			{fieldsMappingPageDescription && (
				<Box xcss={descriptionStyles}>
					<Text color="color.text.subtle">
						{formatMessage(fieldsMappingPageDescription, {
							link: (chunks: ReactNode) => (
								<Link href={fieldURL} target="_blank">
									{chunks}
								</Link>
							),
							customFieldLink: (chunks: ReactNode) => (
								<Link href={customFieldURL} target="_blank">
									{chunks}
								</Link>
							),
						})}
					</Text>
				</Box>
			)}
			{hasUnsupportedFieldFiltered !== false && fieldsMappingPageDisclaimer && (
				<Box xcss={disclaimerStyles}>
					<Inline space="space.075" alignBlock="center">
						<Inline xcss={infoIconWrapper} alignBlock="center">
							<InformationIcon label="" color={token('color.icon.information')} />
						</Inline>
						<Text size="UNSAFE_small" color="color.text.subtle">
							{formatMessage(fieldsMappingPageDisclaimer, {
								link: (chunks: ReactNode) => (
									<Link href={fieldMappingURL} target="_blank">
										{chunks}
									</Link>
								),
							})}
						</Text>
					</Inline>
				</Box>
			)}
		</>
	);
};

const descriptionStyles = xcss({
	marginTop: 'space.100',
});

const disclaimerStyles = xcss({
	marginTop: 'space.150',
});

const infoIconWrapper = xcss({ alignSelf: 'start' });
